import { Box, Button, ButtonAppearance, ButtonProps, ButtonSize, Tab, TabList } from '@bechtel/gds';
import { FC } from 'react';
import { DroppableStateSnapshot } from 'react-beautiful-dnd';
import styled, { createGlobalStyle, css } from 'styled-components';
import { collapsedListWidth, listWidth, smallScreenListWidth } from '../list/styled';

interface BackLogContainerStyledProps {
  isDrawerOpen?: boolean;
}

interface WorkPackageListProps {
  isListCollapsed?: boolean;
}

export const PlanningDetailContainer = styled(Box).attrs({ flexDirection: { sm: 'column' } })<WorkPackageListProps>`
  transition-duration: 160ms;
  transition-timing-function: ease-in;
  transition-property: left;
  position: absolute;
  left: ${listWidth};
  overflow-y: auto;
  height: 100%;
  width: calc(100% - ${listWidth});
  background-color: ${({ theme }) => theme.colors.white};

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.widescreen}) {
    left: ${smallScreenListWidth};
    width: calc(100% - ${smallScreenListWidth});
  }
  ${({ isListCollapsed }) =>
    isListCollapsed &&
    css`
      left: ${collapsedListWidth} !important;
      width: calc(100% - ${collapsedListWidth}) !important;
      box-shadow:
        0 1px 8px 0 rgba(0, 0, 0, 0.2),
        0 3px 3px -2px rgba(0, 0, 0, 0.12),
        0 3px 4px 0 rgba(0, 0, 0, 0.14);
    `}
`;

export const PlanningActionContainer = styled.div`
  ${({ theme }) => css`
    box-shadow:
      0 2px 4px -1px rgba(0, 0, 0, 0.2),
      0 1px 10px 0 rgba(0, 0, 0, 0.12),
      0 4px 5px 0 rgba(0, 0, 0, 0.14);
    background-color: ${theme.colors.white};
  `}
`;
export const PlanningContentContainer = styled(Box).attrs({
  justifyContent: { sm: 'space-between' },
})`
  ${({ theme }) => css`
    background-color: ${theme.colors.grey[200]};
    height: 100vh;
    overflow-y: hidden;
    position: relative;
    padding: ${theme.spacings[500]};
    @media only screen and (max-width: ${theme.breakpoints.widescreen}) {
      padding: ${theme.spacings[400]};
    }
  `}
`;

export const ColumnContainerStyled = styled(Box).attrs({ flexDirection: { sm: 'column' } })`
  ${({ theme }) => css`
    background-color: ${theme.colors.grey[100]};
    border-radius: ${theme.radii[200]};
    box-shadow:
      0 1px 8px 0 rgba(0, 0, 0, 0.2),
      0 3px 3px -2px rgba(0, 0, 0, 0.12),
      0 3px 4px 0 rgba(0, 0, 0, 0.14);
  `}
`;

export const BackLogContainerStyled = styled(ColumnContainerStyled)`
  ${({ theme }) => css`
    margin-right: ${theme.spacings[450]};
    @media only screen and (max-width: ${theme.breakpoints.widescreen}) {
      margin-right: ${theme.spacings[400]};
    }
    ${(props: BackLogContainerStyledProps) =>
      props.isDrawerOpen &&
      css`
        z-index: 1;
        box-shadow:
          0 3px 5px -1px rgba(0, 0, 0, 0.2),
          0 1px 18px 0 rgba(0, 0, 0, 0.12),
          0 6px 10px 0 rgba(0, 0, 0, 0.14);
      `}
  `}
`;

export const WeekPlanDetailContainer = styled(Box)`
  & > :not(:last-child) {
    border-right: dashed 1px ${({ theme }) => theme.colors.grey[200]};
  }
  min-height: 100%;
`;

export const WeekPlanDetailScrollable = styled.div`
  overflow-y: auto;
  height: 100%;
  will-change: transform;
`;

//GDS Box not used here as droppable needs a ref object
export const WeekColumnDroppableStyled = styled.div<DroppableStateSnapshot>`
  display: flex;
  justify-content: 'center';
  transition-duration: 200ms;
  transition-timing-function: ease-in-out;
  transition-property: background-color;
  ${({ theme }) => css`
    padding-top: ${theme.spacings[500]};
    padding-left: ${theme.spacings[450]};
    padding-right: ${theme.spacings[450]};
  `};

  ${({ isDraggingOver }) => css`
    ${isDraggingOver &&
    css`
      background-color: ${({ theme }) => theme.colors.white};
      opacity: 0.8;
    `}
  `}
`;

export const TabListStyled = styled(TabList)`
  box-shadow:
    0 2px 4px -1px rgba(0, 0, 0, 0.2),
    0 1px 10px 0 rgba(0, 0, 0, 0.12),
    0 4px 5px 0 rgba(0, 0, 0, 0.14);
  z-index: 1;
`;

// NUCLEAR OPTION: Force toasts above everything
export const ToastZIndexFix = createGlobalStyle`
  /* AGGRESSIVE: Every possible toast selector */
  body > div,
  body > div > div {
    &[role="status"],
    &[role="region"],
    &[aria-live] {
      z-index: 99999 !important;
      position: fixed !important;
    }
  }

  /* Target by class */
  [class*="Toast"],
  [class*="toast"],
  [class*="notification"] {
    z-index: 99999 !important;
  }

  /* Brute force all fixed divs under body */
  body > div[style*="fixed"] {
    z-index: 99999 !important;
  }
`;

export const PlanningTabStyled = styled(Tab)`
  flex-grow: initial;
  &:focus {
    outline: 0;
  }
  flex-basis: 128px;
`;

export interface TabButtonProps extends ButtonProps {
  selected: boolean;
}

export const TabButtonStyled = styled(Button as FC<TabButtonProps>).attrs({
  appearance: ButtonAppearance.TERTIARY,
  size: ButtonSize.LARGE,
})`
  border: none;
  border-radius: 0;
  position: relative;
  &:focus {
    border-color: transparent;
    box-shadow: none;
  }
  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    color: ${theme.colors.grey[1000]};
    font-size: ${theme.fontSizes['400']};

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 5px;
      background-color: ${theme.colors.blue['700']};
      opacity: 0;
    }
  `};
  ${(props) =>
    props.selected &&
    css`
      font-weight: ${props.theme.fontWeights[600]};
      &::after {
        opacity: 1;
      }
    `};
`;
